// Форматирование значений из Bitrix для показа человеку.

/** Bitrix отдаёт даты в ISO с таймзоной портала. Пустая строка/null → «—». */
export function fmtDate(v?: string | null, withTime = false): string {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return v
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
}

/** Сумма сделки. Bitrix отдаёт строкой («1234.5600»). */
export function fmtMoney(v?: string | number | null, currency = 'RUB'): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return String(v)
  try {
    return n.toLocaleString('ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 })
  } catch {
    // неизвестный код валюты — не падаем, показываем число + код
    return `${n.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ${currency}`
  }
}

export function fmtText(v?: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  return String(v)
}

// ─────────── множественные поля Bitrix (телефон, почта) ───────────
// У контакта их бывает несколько (рабочий, мобильный), поэтому Bitrix отдаёт массив
// объектов `{VALUE, VALUE_TYPE}`, а не строку. Показать `[object Object]` тут проще
// всего и хуже всего, поэтому разбор один на все экраны.

export interface MultiField {
  value: string
  /** WORK, MOBILE, HOME… Bitrix отдаёт код, человеческого названия в ответе нет. */
  type?: string
}

/** Разбирает поле-мультиполе. Терпит и строку: ядро иногда отдаёт уже свёрнутое значение. */
export function multiField(v: unknown): MultiField[] {
  if (v === null || v === undefined || v === '') return []
  if (typeof v === 'string') return [{ value: v }]
  if (!Array.isArray(v)) return []
  const out: MultiField[] = []
  for (const item of v) {
    if (typeof item === 'string') {
      if (item) out.push({ value: item })
      continue
    }
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>
      const value = rec.VALUE
      if (typeof value === 'string' && value) {
        out.push({ value, type: typeof rec.VALUE_TYPE === 'string' ? rec.VALUE_TYPE : undefined })
      }
    }
  }
  return out
}

/** Первое значение мультиполя — для строки списка, где места на все нет. */
export const firstValue = (v: unknown): string => multiField(v)[0]?.value ?? ''

// ─────────── дни (сроки оплаты, просрочка) ───────────
// Просрочку считаем в ЦЕЛЫХ ДНЯХ по календарю, а не в миллисекундах: «просрочен на 1 день»
// должно наступать в полночь, а не через 24 часа после срока. Поэтому работаем со строкой
// «ГГГГ-ММ-ДД» и переводим её в UTC-полдень — так перевод часов и таймзона портала
// не сдвигают разницу на сутки.

const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/

/** День из значения Bitrix: «2026-08-01T00:00:00+03:00» → «2026-08-01». Не дата → null. */
export function isoDay(v?: unknown): string | null {
  if (typeof v === 'number') return null
  if (typeof v !== 'string') return null
  const m = ISO_DAY_RE.exec(v.trim())
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}

/** Сегодня как «ГГГГ-ММ-ДД» по локальной дате пользователя (не по UTC). */
export function todayIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Сколько дней прошло от day до today: >0 — срок в прошлом (просрочка), <0 — впереди. */
export function daysPast(day: string, today = todayIso()): number {
  const a = ISO_DAY_RE.exec(day)
  const b = ISO_DAY_RE.exec(today)
  if (!a || !b) return 0
  const at = Date.UTC(+a[1], +a[2] - 1, +a[3], 12)
  const bt = Date.UTC(+b[1], +b[2] - 1, +b[3], 12)
  return Math.round((bt - at) / 86_400_000)
}

// ─────────── склонение существительного после числа ───────────
//
// ЗАЧЕМ ОТДЕЛЬНОЙ ФУНКЦИЕЙ И ЗАЧЕМ ИМЕННО ЗДЕСЬ. «1 заявок», «2 сделок», «21 записей» —
// это первое, обо что спотыкается глаз, и после такого цифрам рядом верят меньше. Правило
// одно на всю CRM, а копий его было три: `fmtDays` здесь, `plural` в lib/dashboard.ts
// (вместе с правилами кабинета руководителя) и `плюрал` в lib/chain.ts, заведённый только
// потому, что тянуть весь модуль кабинета ради одной функции неправильно. Разъехаться трём
// копиям пока не дали, но третья копия появилась уже с комментарием «удалить, когда
// переедет в lib/format» — значит, четвёртая была вопросом времени.
//
// ПОЧЕМУ РУССКОЕ ПРАВИЛО НЕЛЬЗЯ СВЕСТИ К «n === 1». Форма выбирается по ДВУМ последним
// цифрам, а не по одной: 21 — «заявка», 11 — «заявок», хотя обе оканчиваются на 1.
// Отсюда порядок проверок: сначала «десятки 1» (11–14 — всегда `many`), и только потом
// последняя цифра.

/**
 * Форма существительного после числа: `plural(1, 'заявка', 'заявки', 'заявок')` → «заявка».
 *
 * Само число НЕ подставляется: рядом с ним на экранах то `fmtMoney`, то «по N из M»,
 * то ничего (заголовок «Сделки», подпись «5 шт.»). Печатать его здесь значило бы
 * заводить второй вариант функции на каждый такой случай.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b === 1) return one
  if (b >= 2 && b <= 4) return few
  return many
}

/** «12 дней» / «1 день» — русские окончания, иначе получается «1 дней». */
export function fmtDays(n: number): string {
  return `${n} ${plural(n, 'день', 'дня', 'дней')}`
}

/**
 * Число и существительное вместе: `pluralN(2, 'заявка', 'заявки', 'заявок')` → «2 заявки».
 *
 * Самый частый случай на экранах, и без него в разметке получается `{n} {plural(n, …)}` —
 * три места, где можно забыть пробел или подставить в `plural` другое число, чем показано.
 */
export const pluralN = (n: number, one: string, few: string, many: string): string =>
  `${n} ${plural(n, one, few, many)}`
